"use client"

/**
 * Quick rental dialog — открывается с карточки товара на главной/категории
 * по кнопке «Аренда». Внутри — тот же RentalSection компонент что и на
 * странице деталей товара: календарь с занятыми датами, duration
 * selector, цена за период, проверка доступности на выбранные даты.
 *
 * Полный product объект подтягивается лениво при открытии диалога
 * (карточка передаёт только handle).
 */

import * as React from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import RentalSection from "@modules/products/components/rental-section"
import { useAddRentalToCart } from "@/hooks/use-cart-mutations"
import { toast } from "@/hooks/use-toast"
import { useTranslations } from "next-intl"
import { cartErrorText } from "@lib/util/cart-error-message"
import { useParams, useRouter } from "next/navigation"
import { Loader2 } from "lucide-react"
import { getProductByHandle } from "@lib/data/products"
import type { RentalInfo } from "@/types/rental"
import type { HttpTypes } from "@medusajs/types"

interface QuickRentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productHandle: string
  productTitle: string
  variantIdHint?: string
  rentalInfo: RentalInfo
  regionId?: string
}

export function QuickRentDialog({
  open,
  onOpenChange,
  productHandle,
  productTitle,
  variantIdHint,
  rentalInfo,
  regionId,
}: QuickRentDialogProps) {
  const t = useTranslations()
  const params = useParams()
  const router = useRouter()
  const countryCode = params.countryCode as string
  const addRental = useAddRentalToCart()

  const [product, setProduct] = React.useState<HttpTypes.StoreProduct | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Lazy-load the full product on open. Карточка передаёт только handle/
  // title — нам нужен полный объект с variants/prices для RentalSection.
  React.useEffect(() => {
    if (!open) return
    if (product?.handle === productHandle) return
    let cancelled = false
    setLoading(true)
    setError(null)
    // Server action — fetch goes via storefront's Next.js process and
    // hits MEDUSA_BACKEND_URL on the server. Calling Medusa SDK
    // directly from the client breaks on dev (CORS to localhost:9000)
    // and in prod requires exposing the backend URL as
    // NEXT_PUBLIC_…, which we deliberately don't.
    getProductByHandle({ handle: productHandle, regionId })
      .then((p) => {
        if (cancelled) return
        if (!p) {
          setError(t("product.notFound", { defaultMessage: "Товар не найден" }))
          return
        }
        setProduct(p)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : "fetch failed")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, productHandle, regionId, product?.handle, t])

  const variant = React.useMemo(() => {
    if (!product) return undefined
    if (variantIdHint) {
      return product.variants?.find((v) => v.id === variantIdHint) || product.variants?.[0]
    }
    return product.variants?.[0]
  }, [product, variantIdHint])

  const handleAddRental = async (payload: {
    variantId: string
    rentalPeriod: number
    rentalUnit: string
    startDate: string
    quantity: number
  }) => {
    await new Promise<void>((resolve, reject) => {
      addRental.mutate(
        { ...payload, countryCode },
        {
          onSuccess: () => {
            router.refresh()
            toast({
              title: t("cart.addedToCart"),
              description: productTitle,
            })
            onOpenChange(false)
            resolve()
          },
          onError: (err) => {
            toast({
              title: t("cart.error"),
              description: cartErrorText(t, err, {
                errorsKey: "cart.errors",
                fallback: t("cart.addFailed"),
              }),
              variant: "destructive",
            })
            // The toast already explains it — resolve so the caller doesn't surface the raw
            // (English) message a second time and the dev overlay doesn't flag a handled refusal.
            resolve()
          },
        }
      )
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{productTitle}</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {t("common.loading", { defaultMessage: "Загрузка…" })}
          </div>
        )}

        {error && !loading && (
          <div className="text-destructive text-sm py-6 text-center">{error}</div>
        )}

        {product && !loading && !error && (
          <RentalSection
            product={product}
            variant={variant}
            rentalInfo={rentalInfo}
            onAddRental={handleAddRental}
            isAdding={addRental.isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default QuickRentDialog
